<template>
  <div class="import-youzan-container">
    <div class="page-header">
      <h2>有赞会员导入</h2>
      <p class="desc">按时间段从有赞拉取会员数据并导入会员中心，适用于初始导入和补漏。按会员成为时间范围拉取，自动判断会员中心是否存在，存在则更新，不存在则创建。</p>
    </div>

    <!-- 时间范围选择 -->
    <div class="form-section">
      <el-form :model="form" label-width="100px" style="max-width: 600px">
        <el-form-item label="手机号批量">
          <el-input
            v-model="form.phones"
            type="textarea"
            :rows="6"
            placeholder="最多50个，一行一个手机号&#10;有手机号时优先使用手机号导入，无需填写时间范围"
            maxlength="600"
            show-word-limit
          />
          <div class="input-hint">已输入 {{ phoneCount }} 个手机号 / 最多50个</div>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="form.timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          />
          <div class="input-hint" v-if="phoneCount === 0">未填写手机号时，必须选择时间范围</div>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            开始同步
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 同步进度 -->
    <div v-if="taskId" class="progress-section">
      <h3>同步进度</h3>

      <!-- 处理中：循环动画进度条 -->
      <div v-if="taskStatus.status === 'RUNNING'" class="loading-progress">
        <div class="progress-bar-cycling"></div>
        <div class="progress-message">{{ taskStatus.message || '正在同步中...' }}</div>
      </div>

      <!-- 处理完成/异常：显示结果 -->
      <div v-else class="progress-result">
        <el-progress
          :percentage="100"
          :status="taskStatus.status === 'DONE' ? 'success' : 'exception'"
          :stroke-width="20"
        ></el-progress>
        <div class="progress-info">
          <span>总计: <strong>{{ taskStatus.total }}</strong></span>
          <span>已处理: <strong>{{ taskStatus.processed }}</strong></span>
          <span class="text-success">成功: <strong>{{ taskStatus.success }}</strong></span>
          <span class="text-danger">失败: <strong>{{ taskStatus.fail }}</strong></span>
        </div>
        <div v-if="taskStatus.message" class="progress-message">{{ taskStatus.message }}</div>
      </div>

      <!-- 完成操作 -->
      <div v-if="taskStatus.status === 'DONE' || taskStatus.status === 'ERROR'" class="action-buttons">
        <el-button type="primary" icon="el-icon-refresh" @click="resetImport">重新同步</el-button>
        <el-button icon="el-icon-user" @click="$router.push('/member')">查看会员列表</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { syncFromYouzan, getYouzanImportTask } from '@/api/member'

export default {
  name: 'MemberImportYouzan',
  data() {
    return {
      form: {
        phones: '',
        timeRange: []
      },
      submitting: false,
      taskId: null,
      taskStatus: {
        total: 0,
        processed: 0,
        success: 0,
        fail: 0,
        status: '',
        message: ''
      },
      pollingTimer: null
    }
  },
  computed: {
    phoneCount() {
      if (!this.form.phones || this.form.phones.trim() === '') return 0
      return this.form.phones.split('\n').filter(p => p.trim() !== '').length
    },
    canSubmit() {
      // 有手机号时可提交，无手机号时必须选时间范围
      if (this.phoneCount > 0) return true
      return this.form.timeRange && this.form.timeRange.length === 2
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async handleSubmit() {
      if (!this.canSubmit) {
        this.$message.warning('请填写手机号或选择时间范围')
        return
      }

      const payload = {}
      // 手机号优先
      if (this.phoneCount > 0) {
        const phones = this.form.phones.split('\n').map(p => p.trim()).filter(p => p !== '')
        if (phones.length > 50) {
          this.$message.warning('手机号最多50个')
          return
        }
        payload.phones = phones
      } else {
        if (!this.form.timeRange || this.form.timeRange.length !== 2) {
          this.$message.warning('请选择时间范围')
          return
        }
        payload.startTime = this.form.timeRange[0]
        payload.endTime = this.form.timeRange[1]
      }

      this.submitting = true
      try {
        const res = await syncFromYouzan(payload)
        this.taskId = res.data.taskId
        this.taskStatus = { total: 0, processed: 0, success: 0, fail: 0, status: 'RUNNING', message: '正在同步中...' }
        this.$message.success('同步任务已提交，正在处理中...')
        this.startPolling()
      } catch (e) {
        let msg = '提交失败，请稍后重试'
        if (e.response && e.response.data && e.response.data.message) {
          msg = e.response.data.message
        } else if (e.code === 'ECONNABORTED' || (e.message && e.message.includes('timeout'))) {
          msg = '请求超时，请稍后重试'
        }
        this.$message.error('提交同步任务失败: ' + msg)
      } finally {
        this.submitting = false
      }
    },

    startPolling() {
      this.stopPolling()
      // 立即查询一次，再定时轮询
      this.pollTask()
      this.pollingTimer = setInterval(() => this.pollTask(), 3000)
    },

    async pollTask() {
      try {
        const res = await getYouzanImportTask(this.taskId)
        if (!res.data) return

        // 逐个属性赋值确保Vue响应式更新
        this.taskStatus.total = res.data.total
        this.taskStatus.processed = res.data.processed
        this.taskStatus.success = res.data.success
        this.taskStatus.fail = res.data.fail
        this.taskStatus.status = res.data.status
        this.taskStatus.message = res.data.message

        console.log('[pollTask] status:', this.taskStatus.status, 'total:', this.taskStatus.total)

        if (res.data.status === 'DONE' || res.data.status === 'ERROR') {
          this.stopPolling()
        }
      } catch (e) {
        console.error('轮询进度失败:', e)
      }
    },

    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    resetImport() {
      this.taskId = null
      this.taskStatus = { total: 0, processed: 0, success: 0, fail: 0, status: '', message: '' }
      this.form.phones = ''
      this.form.timeRange = []
      this.stopPolling()
    }
  }
}
</script>

<style lang="less" scoped>
.import-youzan-container {
  padding: 20px;
  .page-header {
    margin-bottom: 24px;
    h2 {
      font-size: 20px;
      margin: 0 0 8px;
    }
    .desc {
      color: #909399;
      font-size: 13px;
      margin: 0;
    }
  }
  .form-section {
    margin-bottom: 24px;
    .input-hint {
      color: #909399;
      font-size: 12px;
      margin-top: 4px;
    }
  }
  .progress-section {
    h3 {
      font-size: 16px;
      margin: 0 0 16px;
    }
    .loading-progress {
      .progress-bar-cycling {
        height: 20px;
        border-radius: 10px;
        background: #EBEEF5;
        position: relative;
        overflow: hidden;
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 30%;
          height: 100%;
          background: #409EFF;
          border-radius: 10px;
          animation: cycling-progress 2s ease-in-out infinite;
        }
      }
      .progress-message {
        margin-top: 8px;
        color: #606266;
        font-size: 13px;
        text-align: center;
      }
    }
    .progress-result {
      .progress-info {
        display: flex;
        gap: 16px;
        margin-top: 12px;
        font-size: 14px;
        span { white-space: nowrap; }
      }
      .progress-message {
        margin-top: 8px;
        color: #606266;
        font-size: 13px;
      }
    }
    .action-buttons {
      margin-top: 20px;
    }
  }
  .text-success { color: #67C23A; }
  .text-danger { color: #F56C6C; }

  @keyframes cycling-progress {
    0% { left: -30%; }
    50% { left: 100%; }
    100% { left: -30%; }
  }
}
</style>
